export const ORGANIZATION_SESSIONS_GUIDE = `# Organization Sessions - Practitioner Guide

## Overview

**Organization Accounts** enable you to provide personalized meditation sessions to employees of partner organizations as part of their corporate wellness programs. These sessions blend **individual employee context** with **organizational values and culture**, making employees feel both personally understood AND valued as team members.

### Key Benefits
- **Dual-Context Sessions:** Automatically combines employee biography + organization culture
- **Scalable Wellness Programs:** Manage multiple organizations and hundreds of employees
- **Professional Acknowledgment:** Every session credits the organization's investment in employee wellbeing
- **Department-Aware:** Sessions reference team dynamics and role-specific challenges

---

## Setup Guide

### 1. Creating an Organization Account

Organizations must first create their own accounts to establish their wellness profile:

1. Navigate to \`/organization/signup\`
2. Fill out organization details:
   - **Organization Name** (required)
   - **Organization Type:** Corporate, Non-Profit, Educational, Healthcare, Government, etc.
   - **Industry** (optional)
   - **Size:** 1-10, 11-50, 51-200, 201-500, 501-1000, 1000+ employees
   - **Organization Biography** (up to 5000 chars): Company history, culture, what makes them unique
   - **Mission Statement:** Their core purpose and goals
   - **Core Values:** Fundamental beliefs and principles (array of values)
   - **Culture Description:** Day-to-day work environment and team dynamics
   - **Wellness Philosophy:** Their approach to employee wellbeing
3. Click **Create Organization**
4. Organization owner receives access to dashboard at \`/organization/dashboard\`

**Note:** Only the organization owner/admin can initially create the account. You (the practitioner) will link to their existing organization profile.

---

### 2. Adding Employees and Departments

**Organization admins** manage their employee roster through the organization dashboard:

#### Creating Departments
1. Navigate to **Organization Dashboard → Departments Tab**
2. Click **Add Department**
3. Enter:
   - **Department Name** (e.g., "Engineering", "Marketing", "Operations")
   - **Department Description** (optional)
   - **Department Head** (optional)
4. Click **Create Department**

**Important:** At least one department must exist before adding employees.

#### Adding Employees
1. Navigate to **Organization Dashboard → Employees Tab**
2. Click **Add Employee**
3. Fill out employee profile:
   - **User Account:** Link to existing VoiceThera user or enter email
   - **Preferred Name** (optional)
   - **Name Usage Preference:** Full Name, First Name Only, Preferred Name
   - **Employee Role:** Job title (e.g., "Senior Software Engineer", "Marketing Manager")
   - **Department(s):** Assign to one or more departments (mark one as primary)
   - **Employee Biography** (up to 3000 chars): Personal background, work style, challenges, interests
4. Click **Add Employee**

**Employee Biography Best Practices:**
- Include work history and career journey
- Note personality traits and communication style
- Mention hobbies, interests outside of work
- Highlight current challenges or stress factors
- Reference family situation if relevant
- Keep tone professional but personal

---

### Linking Practitioner Clients to Organization Employees

As a practitioner, you can link your clients to organization employees:

1. Navigate to **Practitioner Dashboard → Clients Tab**
2. Click **Add Client** or edit existing client
3. Select **Client Type: "Organization Employee"**
4. Choose from dropdowns:
   - **Organization:** Select the partner organization (you can only see organizations where you are an owner, employee, or have been granted access)
   - **Employee:** Select the specific employee from that organization
5. **Auto-Population:** The following fields will automatically populate:
   - First Name, Last Name
   - Employee Biography
   - Department
   - Employee Role
6. **Organization Context Preview:** You'll see:
   - Organization Name
   - Core Values
   - Mission Statement
   - Wellness Philosophy
7. Click **Save Client**

**Important Notes:**
- The client must be marked as **"Organization Employee"** type
- You can only link to organizations where you are an owner, employee, or have been granted practitioner access
- Employee biographies are maintained by the organization, not by you
- You can add session directives, content requirements, and exclusions as usual

### Granting Practitioner Access

If you work with external practitioners who are not employees of your organization, organization admins can grant them access:

**For Organization Admins:**
1. Go to **Organization Dashboard → Settings Tab**
2. Scroll to **Practitioner Access** section
3. Click **Grant Access**
4. Enter the practitioner's email address
5. Optionally set an expiration date for the access
6. Add notes about why this access is being granted
7. Click **Grant Access**

The practitioner will now be able to:
- See your organization in their client creation form
- Link clients to your organization
- Generate organization-contextualized sessions for those clients

**Revoking Access:**
- Organization admins can revoke access at any time from the Settings tab
- Revoking access prevents creating NEW client links
- Existing clients remain linked and sessions can still be generated for them

**For Practitioners:**
- View organizations that have granted you access in **Practitioner Dashboard → My Organization Access** card
- You'll see organization name, type, status, and any expiration dates
- **Important:** Only organizations with **Active** status will appear in the client creation form
- If you see a grant marked as "Pending", contact the organization admin to activate it

---

## Creating Sessions for Organization Clients

Once you've linked a client to an organization employee, creating sessions is straightforward—the organization context is applied **automatically**.

### Step-by-Step Workflow

1. **Navigate to Session Creation:**
   - Go to **Practitioner Dashboard → Sessions Tab**
   - Click **Create Session** or select an existing organization-linked client

2. **Select Your Organization Client:**
   - Choose the client you previously linked to an organization employee
   - The system automatically detects their organization affiliation

3. **Choose Meditation Category:**
   - Select any category: Zen AF, News Meditation, or any other available category
   - **All categories support organization context** - the integration is seamless across the entire platform

4. **Fill Out Questionnaire:**
   - Complete the category-specific questionnaire as usual
   - You'll see the **Organization Session Preview Card** displaying:
     - Organization name and employee details
     - Core organizational values
     - Mission statement
     - Example opening acknowledgment
   - This confirms the organization context is loaded correctly

5. **Generate Session:**
   - Click **Generate Session**
   - The AI automatically:
     - Fetches the organization's biography, values, mission, culture, and wellness philosophy
     - Blends them with the employee's personal biography
     - Creates dual-context therapeutic content
     - Ensures mandatory organizational acknowledgment in opening
     - Weaves organizational values throughout the session

6. **Review and Share:**
   - Preview the generated session
   - Sessions are automatically tagged with \`organization_id\` and \`created_as_employee\` flag
   - Share with your client as usual

### What Happens Automatically

When you create a session for an organization-linked client:

- ✅ **Organization context is fetched** - No manual input required
- ✅ **Dual-context blending** - Employee biography + organizational values
- ✅ **Mandatory acknowledgment** - "This wellness moment is brought to you by [Organization Name]..."
- ✅ **Values integration** - 3-4 explicit connections to organizational values throughout
- ✅ **Works across all categories** - Zen AF, News Meditation, and future categories
- ✅ **Billing/tracking** - Sessions tagged for organizational attribution

### What You DON'T Need to Do

- ❌ Manually enter organization details
- ❌ Copy/paste organizational values
- ❌ Select a special "organization mode"
- ❌ Use different questionnaires
- ❌ Modify your normal workflow

**The system handles everything automatically based on the client's organization link.**

### Important Notes

- **Personal mode remains unchanged**: If you create a session for a non-organization client, only their personal biography is used—zero organization context
- **Context loading**: The organization context must fully load before generation begins. If you see a loading state, wait for it to complete
- **All categories supported**: Organization context integration works for Zen AF, News Meditation, and any future categories—no special handling needed

---

## Session Generation

### How Dual-Context Sessions Work

When you generate a session for an organization-linked client, the AI automatically blends:

**Individual Context:**
- Employee's personal biography
- Current emotional state (from questionnaire)
- Role-specific challenges
- Department dynamics

**Organizational Context:**
- Organization name and identity
- Core values and mission
- Wellness philosophy
- Cultural tone

### Session Structure

Every organization session includes:

1. **Opening Acknowledgment (First 250-300 characters):**
   > "This wellness moment is brought to you by [Organization Name], a company that values [core value]. They've invested in your wellbeing because they recognize [insight about employee value]..."

2. **Personal Validation Through Organizational Values:**
   - Connects employee's struggle to organization's values
   - Example: "As [Organization] emphasizes innovation, they understand the mental load of problem-solving..."

3. **Mission Alignment:**
   - Links personal growth to organizational mission
   - Example: "Your recovery isn't just personal—it fuels [Organization]'s mission to [mission statement]..."

4. **Team Member Recognition:**
   - Positions employee as valued contributor
   - Example: "You're not just an employee—you're a vital part of [Organization]'s [department] team..."

5. **No Corporate Jargon:**
   - Avoids HR-speak, buzzwords, or performative language
   - Maintains therapeutic warmth and authenticity

---

### Organization Context Preview

Before generating a session, you'll see the **Organization Session Preview** card in Step 2 (Questionnaire):

**Preview Shows:**
- Organization name and employee details
- Core values (first 3 if more than 3)
- Mission statement (truncated if >200 chars)
- Wellness philosophy (truncated if >200 chars)
- Example opening acknowledgment
- Blending strategy explanation

**This helps you:**
- Verify correct organization context is loaded
- Understand how the session will acknowledge the organization
- Ensure organizational values align with session goals

---

## Best Practices

### For Practitioners

1. **Communicate with Organizations:**
   - Explain that employee biographies are crucial for personalization
   - Encourage detailed, honest employee profiles
   - Clarify that sessions are confidential between practitioner and employee

2. **Session Selection:**
   - Choose categories that align with organizational wellness goals
   - Consider department-specific stressors (e.g., "Deadline Pressure" for Engineering)
   - Use group sessions for team-building initiatives

3. **Quality Control:**
   - Review generated sessions before delivery
   - Verify organizational acknowledgment appears naturally
   - Ensure no corporate jargon slipped through
   - Confirm values are mentioned authentically

4. **Feedback Loop:**
   - Collect employee feedback on session relevance
   - Report patterns to organization admins
   - Adjust session directives based on what resonates

### For Organizations

1. **Employee Biography Quality:**
   - Encourage employees to be honest and detailed
   - Include both professional and personal context
   - Update biographies quarterly or after major life changes

2. **Department Structure:**
   - Create meaningful departments that reflect team dynamics
   - Assign employees to departments that match their daily work
   - Update department heads when leadership changes

3. **Core Values Authenticity:**
   - Ensure core values are lived, not just stated
   - Avoid corporate jargon in values
   - Keep values list focused (3-7 core values)

---

## Examples

### Example 1: Tech Company - Stress Relief Session

**Organization Profile:**
- **Name:** InnovateTech Solutions
- **Core Values:** Innovation, Work-Life Balance, Continuous Learning
- **Mission:** "Empower businesses through cutting-edge software while maintaining a sustainable work culture"
- **Wellness Philosophy:** "We believe productive teams are rested teams"

**Employee Profile:**
- **Name:** Sarah Chen
- **Role:** Senior Software Engineer
- **Department:** Backend Engineering
- **Biography:** "5-year veteran developer, loves solving complex problems but struggles with burnout during sprint cycles. Single mom of 2, practices yoga on weekends, prefers direct communication."

**Generated Session Opening:**
> "This wellness moment is brought to you by InnovateTech Solutions, a company that genuinely values work-life balance—not as a buzzword, but as a lived commitment to your sustainability. They've invested in this session because they recognize that your brilliance in backend engineering comes at a cost, and you deserve recovery as much as you deserve recognition. Sarah, as a senior engineer grinding through another sprint cycle, you know the mental weight of complex problem-solving. InnovateTech's mission to empower businesses through cutting-edge software starts with empowering you—because innovation thrives when minds are rested, not fried..."

---

### Example 2: Non-Profit - Sleep Session

**Organization Profile:**
- **Name:** Community Care Alliance
- **Core Values:** Compassion, Equity, Community Impact
- **Mission:** "Provide accessible healthcare to underserved communities"
- **Wellness Philosophy:** "Caregivers need care too"

**Employee Profile:**
- **Name:** Marcus Johnson
- **Role:** Outreach Coordinator
- **Department:** Community Programs
- **Biography:** "Former social worker, passionate about health equity, often works evenings and weekends for community events. Struggles with insomnia due to emotionally heavy work. Lives with partner, enjoys hiking."

**Generated Session Opening:**
> "This moment of rest is brought to you by Community Care Alliance, an organization that understands that caregivers need care too. They've created this space for you because they know that compassion—their deepest value—starts with self-compassion. Marcus, as an outreach coordinator carrying the emotional weight of community impact work, you give so much of yourself to underserved communities. But tonight, Community Care Alliance is caring for you. Your mission to provide accessible healthcare doesn't require you to sacrifice your own rest. Let's reclaim the sleep your body desperately needs..."

---

### Example 3: Corporate - Fitness/Yoga Session

**Organization Profile:**
- **Name:** Summit Financial Group
- **Core Values:** Integrity, Excellence, Teamwork
- **Mission:** "Deliver trusted financial guidance with uncompromising ethics"
- **Wellness Philosophy:** "Peak performance requires holistic health"

**Employee Profile:**
- **Name:** Jennifer Lee
- **Role:** Financial Analyst
- **Department:** Investment Research
- **Biography:** "Detail-oriented analyst, 3 years at Summit, sits at desk 8-10 hours daily, chronic lower back pain, used to run marathons but stopped due to injury, wants to rebuild strength gradually."

**Generated Session Opening:**
> "This movement practice is brought to you by Summit Financial Group, a company that believes peak performance requires holistic health—not just mental sharpness, but physical vitality. They've invested in your recovery because they understand that excellence, one of their core values, includes taking care of the body that carries your brilliant analytical mind. Jennifer, after years of hunching over financial models in investment research, your lower back is screaming for relief. Summit's commitment to integrity extends to the integrity of your physical wellbeing. Let's rebuild the strength you once had, gradually and safely, because your body deserves the same meticulous attention you give to those research reports..."

---

## Troubleshooting

### Issue: Organization Context Not Showing

**Problem:** When creating a session for an org-linked client, the Organization Session Preview doesn't appear.

**Solutions:**
1. Verify the client is marked as **"Organization Employee"** type
2. Confirm \`organization_id\` is set on the client record
3. Check that the organization profile is complete (name, values, mission)
4. Refresh the page and try again
5. Check browser console for errors

---

### Issue: Session Missing Organizational Acknowledgment

**Problem:** Generated audio doesn't include "This session is brought to you by [Organization]..." in the opening.

**Solutions:**
1. Verify the edge function received \`organizationContext\` in the request payload
2. Check that \`organizationName\` is not null/empty
3. Regenerate the session (may have been a generation error)
4. Report to support if issue persists

---

### Issue: Auto-Population Not Working

**Problem:** When selecting an organization employee, the client form fields don't auto-populate.

**Solutions:**
1. Ensure the employee profile is complete (name, biography, department, role)
2. Verify the employee is marked as **active** in the organization
3. Check that you have permission to view that organization's employees
4. Try refreshing the organization/employee dropdowns
5. Clear browser cache and reload

---

### Issue: Corporate Jargon in Sessions

**Problem:** Generated session uses HR-speak or performative language like "synergy", "leverage", "key stakeholder".

**Solutions:**
1. Check organization's core values and mission—avoid jargon in those fields
2. Review wellness philosophy for corporate buzzwords
3. Add jargon terms to **Content Exclusions** for that client
4. Regenerate the session
5. Provide feedback to VoiceThera team for AI improvement

---

### Issue: Employee Biography Too Generic

**Problem:** Sessions feel impersonal despite organizational context.

**Solutions:**
1. Work with organization admin to enhance employee biographies
2. Include specific challenges, hobbies, personality traits
3. Reference current projects or stressors
4. Mention family situation, outside interests
5. Update biographies quarterly

---

## Advanced Features (Coming Soon)

### Employee Self-Service Portal
- Employees can generate their own sessions with organizational context
- Direct access without practitioner mediation
- Organization admin can enable/disable self-service

### Usage Analytics
- Track session generation by department
- Identify most-requested session types
- Monitor employee engagement with wellness program

### Custom Organizational Audio Branding
- Upload organization-specific intro/outro clips
- Custom background music aligned with brand
- Branded session thumbnails

---

## Support

**For Practitioners:**
- Contact VoiceThera Support: support@voicethera.com
- Join Practitioner Community
- Submit Feature Requests

**For Organizations:**
- Organization Dashboard Help
- Employee Onboarding Guide
- Wellness Program Consultation: partnerships@voicethera.com

---

## Legal & Privacy

- **Compliance:** All sessions are encrypted and confidential
- **Employee Consent:** Organizations must obtain employee consent for wellness program participation
- **Data Ownership:** Employee biographies are owned by the organization; session data is owned by the practitioner/employee
- **Access Control:** Only authorized practitioners and organization admins can view employee profiles
- **Audit Logging:** All access to employee data is logged for security

---

**Last Updated:** January 2025
**Version:** 1.0.0
**Feature Status:** Production-Ready
`;
